import { useContext } from 'react'
import { useTranslation } from 'libs/i18n'
import { useNavigate } from 'libs/router'
import { useUserRoles } from 'shared/hooks'
import { StoreSettingsContext } from 'store'
import type { Option } from 'shared/components/ContextMenu/types'
import type { ProductItem } from 'features/products/types/products.types'
import { useConfirmProductMutation } from 'features/products/mutations/useConfirmProductMutation'
import { useProductPath } from './useProductPath'

type UseContextMenuOptionsProps = {
	handlePrint: (id: number) => void
	handleDelete: (id: number) => void
	updateProductStatusMutation: ({ id, published }: { id: number; published: boolean }) => void
}

export const useContextMenuOptions = ({
	handlePrint,
	handleDelete,
	updateProductStatusMutation
}: UseContextMenuOptionsProps) => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const productPath = useProductPath()
	const { isStaff, isUser } = useUserRoles()
	const storeCtx = useContext(StoreSettingsContext)
	const { confirmProductMutation, unconfirmProductMutation } = useConfirmProductMutation()

	const canPublish = (product: ProductItem) =>
		product.published ||
		!!isStaff ||
		(!!storeCtx?.storeSettings?.users_can_publish_products &&
			(!storeCtx?.storeSettings?.photos_required_to_publish || !!product.image_thumbnail))

	return (product: ProductItem): Option[] => [
		{
			label: t('Edit'),
			action: () => navigate(productPath(product.id))
		},
		...(isStaff
			? [
					{
						label: t('Print'),
						action: () => handlePrint(product.id)
					}
				]
			: []),
		...(product.confirmed != null && isUser && !product.confirmed
			? [
					{
						label: t('Confirm'),
						action: () => confirmProductMutation(String(product.id))
					}
				]
			: []),
		...(product.confirmed != null && isStaff && product.confirmed
			? [
					{
						label: t('Unconfirm'),
						action: () => unconfirmProductMutation(String(product.id))
					}
				]
			: []),
		{
			label: product.published ? t('Unpublish') : t('Publish'),
			disabled: !canPublish(product),
			action: () => updateProductStatusMutation({ id: product.id, published: !product.published })
		},
		{
			label: t('Delete'),
			action: () => handleDelete(product.id)
		}
	]
}
